import { useState, useEffect } from "react";
import { Link, useParams } from "react-router";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import {
  fetchWorkshopSessionById,
  fetchWorkshopSubscriptionsBySession,
  updateWorkshopSubscriptionStatus,
  type WorkshopSessionWithCount,
  type WorkshopSubscription,
} from "../supabase";
import { useAdminAuth } from "./AdminAuthContext";
import { ArrowLeft } from "lucide-react";

const columnHelper = createColumnHelper<WorkshopSubscription>();

export function AdminWorkshopSubscribers() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { hasPermission } = useAdminAuth();
  const [session, setSession] = useState<WorkshopSessionWithCount | null>(null);
  const [subscribers, setSubscribers] = useState<WorkshopSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);
  const [globalFilter, setGlobalFilter] = useState("");

  useEffect(() => {
    if (!sessionId || !hasPermission("workshops")) return;
    fetchWorkshopSessionById(sessionId).then(({ data }) => setSession(data ?? null));
    fetchWorkshopSubscriptionsBySession(sessionId).then(({ data, error }) => {
      setLoading(false);
      setSubscribers(data ?? []);
    });
  }, [sessionId, hasPermission]);

  if (!hasPermission("workshops")) {
    return <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>You don&apos;t have access.</p>;
  }

  const columns = [
    columnHelper.accessor("created_at", {
      header: "Date",
      cell: (info) => new Date(info.getValue()).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }),
    }),
    columnHelper.accessor("first_name", { header: "First name", cell: (info) => info.getValue() ?? "—" }),
    columnHelper.accessor("last_name", { header: "Last name", cell: (info) => info.getValue() ?? "—" }),
    columnHelper.accessor("email", { header: "Email", cell: (info) => info.getValue() }),
    columnHelper.accessor("phone", { header: "Phone", cell: (info) => info.getValue() ?? "—" }),
    columnHelper.accessor("company", { header: "Company", cell: (info) => info.getValue() ?? "—" }),
    columnHelper.accessor("status", { header: "Status", cell: (info) => info.getValue() }),
    columnHelper.accessor("marketing_opt_in", { header: "Marketing", cell: (info) => (info.getValue() ? "Yes" : "No") }),
    columnHelper.accessor("notes", { header: "Notes", cell: (info) => (info.getValue() ? String(info.getValue()).slice(0, 40) + (String(info.getValue()).length > 40 ? "…" : "") : "—") }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <select
          value={row.original.status}
          onChange={async (e) => {
            await updateWorkshopSubscriptionStatus(row.original.id, e.target.value);
            fetchWorkshopSubscriptionsBySession(sessionId!).then(({ data }) => setSubscribers(data ?? []));
          }}
          className="px-2 py-1 border border-[#E6E0D7] rounded text-xs"
        >
          <option value="subscribed">Subscribed</option>
          <option value="attended">Attended</option>
          <option value="no_show">No show</option>
          <option value="cancelled">Cancelled</option>
          <option value="waitlist">Waitlist</option>
        </select>
      ),
    }),
  ];

  const table = useReactTable({
    data: subscribers,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const exportCsv = () => {
    const headers = ["created_at", "first_name", "last_name", "email", "phone", "company", "status", "notes"];
    const rows = subscribers.map((s) => headers.map((h) => (s as Record<string, unknown>)[h] ?? "").map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `workshop-subscribers-${sessionId}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const title = session ? (session.title_override || session.template_title) : "Session";

  return (
    <div>
      <Link to="/admin/workshops" className="inline-flex items-center gap-2 text-[#6F675F] hover:text-[#6F7F63] mb-6" style={{ fontSize: "13px" }}>
        <ArrowLeft size={16} /> Back to sessions
      </Link>
      <h1 className="text-[#2D2A26] tracking-[0.08em] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}>
        Subscribers — {title}
      </h1>
      <p className="text-[#6F675F] mb-2" style={{ fontSize: "13px" }}>
        {session && new Date(session.starts_at).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}
      </p>
      <p className="text-[#6F675F] mb-6" style={{ fontSize: "12px" }}>
        When the session is full, new signups join the <strong>waitlist</strong>. You can notify waitlist subscribers when a spot opens (e.g. via RESEND when configured).
      </p>

      <div className="flex flex-wrap gap-3 mb-6">
        <input
          type="text"
          placeholder="Search…"
          value={globalFilter ?? ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="px-4 py-2.5 border border-[#E6E0D7] rounded-lg max-w-xs"
          style={{ fontSize: "13px" }}
        />
        <button type="button" onClick={exportCsv} className="px-4 py-2.5 border border-[#E6E0D7] rounded-lg text-[#6F675F] hover:bg-[#F2EEE8]" style={{ fontSize: "12px" }}>
          Export CSV
        </button>
      </div>

      {loading ? (
        <p className="text-[#6F675F]">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b border-[#E6E0D7]">
                  {hg.headers.map((h) => (
                    <th key={h.id} className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>
                      <span className={h.column.getCanSort() ? "cursor-pointer" : ""} onClick={() => h.column.toggleSorting()}>
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getIsSorted() && (h.column.getIsSorted() === "asc" ? " ↑" : " ↓")}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-[#E6E0D7]/50 hover:bg-[#F2EEE8]/50">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-5 py-3" style={{ fontSize: "13px" }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {subscribers.length === 0 && !loading && <p className="text-[#6F675F] mt-6">No subscribers yet.</p>}
    </div>
  );
}
